import React, { memo, useCallback, useMemo } from 'react'

import { Button } from '@cgtrader/triangle-ui/dist/src/ssr-index'

import PriceContent from 'Pods/SearchPage/Shared/PriceContent/PriceContent'
import { priceRangeOptions } from 'Pods/SearchPage/Shared/PriceContent/RangeButtons/constants'
import useTranslate from 'Pods/SearchPage/Shared/useTranslate'
import { clearPricing } from 'Pods/SearchPage/Store/actions/pricing'
import { DEFAULT_STATE, SUBSCRIPTION_TIER } from 'Pods/SearchPage/Store/constants'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'
import FiltersWrapper from 'Pods/Shared/FiltersWrapper/FiltersWrapper'

// Plan names stay untranslated, as in the SubscriptionTier radio group.
const SUBSCRIPTION_TIER_LABELS = {
  [SUBSCRIPTION_TIER.ESSENTIALS]: 'Essentials',
  [SUBSCRIPTION_TIER.PREMIUM]: 'Premium',
}

const isPriceBoundSet = (value, defaultValue) => value !== defaultValue && value !== 0

// Same shapes as the range buttons ("$2 - $10", "$100+").
const formatCustomPriceRange = (minPrice, maxPrice) => {
  const hasMin = isPriceBoundSet(minPrice, DEFAULT_STATE.minPrice)
  const hasMax = isPriceBoundSet(maxPrice, DEFAULT_STATE.maxPrice)

  if (hasMin && hasMax) return `$${minPrice} - $${maxPrice}`
  if (hasMin) return `$${minPrice}+`
  if (hasMax) return `$0 - $${maxPrice}`

  return null
}

function Price() {
  const translate = useTranslate()

  const onSale = useSearchPageStore(state => state.onSale)
  const withSubscription = useSearchPageStore(state => state.withSubscription)
  const subscriptionTier = useSearchPageStore(state => state.subscriptionTier)
  const priceRanges = useSearchPageStore(state => state.priceRanges)
  const minPrice = useSearchPageStore(state => state.minPrice)
  const maxPrice = useSearchPageStore(state => state.maxPrice)

  // The subscription tier leads the label (Figma 2760:137659, "Price: Essentials +1").
  const selectedValues = useMemo(() => {
    const values = []

    if (withSubscription) {
      values.push(
        SUBSCRIPTION_TIER_LABELS[subscriptionTier] ?? translate('search_page.filters.price.with_subscription')
      )
    }
    if (onSale) {
      values.push(translate('search_page.filters.price.on_sale'))
    }
    priceRangeOptions
      .filter(option => priceRanges?.includes(option.value))
      .forEach(option => values.push(translate(`search_page.filters.price.ranges.${option.labelKey}`)))

    const customPriceRange = formatCustomPriceRange(minPrice, maxPrice)
    if (customPriceRange) {
      values.push(customPriceRange)
    }

    return values
  }, [onSale, withSubscription, subscriptionTier, priceRanges, minPrice, maxPrice, translate])

  const hasSelectedAnyFilter = selectedValues.length > 0

  const handleReset = useCallback(() => {
    clearPricing()
  }, [])

  const content = useMemo(() => (
    <>
      <PriceContent />
      <Button
        type="ghost"
        size="tiny"
        customClass="filter-wrapper__reset-button"
        onClick={handleReset}
        disabled={!hasSelectedAnyFilter}
      >
        {translate('search_page.filters.more_filters.reset')}
      </Button>
    </>
  ), [handleReset, hasSelectedAnyFilter, translate])

  return (
    <FiltersWrapper
      trailIconSize="small"
      placeholder={translate('search_page.filters.price.title')}
      content={content}
      hasSelectedAnyFilters={hasSelectedAnyFilter}
      selectedValues={selectedValues}
      closingDropdownClassName="filter-wrapper"
    />
  )
}

export default memo(Price)
